import {
  AdminPricingApiService,
  CABINET_PATHS,
  type AdminDomainPricingVersionListResponse,
  type AdminDomainPricingVersionSummary,
  type PricingDomain,
} from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, forkJoin, map, of, type Observable } from 'rxjs';
import { bindStaffPageTitleCount } from './staff-page-title-count.util';
import {
  buildUnifiedPricingTimeline,
  formatPricingDate,
  formatPricingRate,
  isPricingVersionCurrent,
  isPricingVersionFuture,
  PRICING_DOMAINS,
} from './staff-pricing.util';

type DomainListResult =
  | { ok: true; response: AdminDomainPricingVersionListResponse }
  | { ok: false; domain: PricingDomain };

@Component({
  selector: 'lib-staff-pricing-hub',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, RouterLink],
  templateUrl: './staff-pricing-hub.component.html',
  styleUrl: './staff-pricing-hub.component.scss',
})
export class StaffPricingHubComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #pricingApi = inject(AdminPricingApiService);

  protected readonly staffCabinetPath = this.#cabinetPaths.staff;
  protected readonly domains = PRICING_DOMAINS;

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly partialLoadError = signal(false);
  protected readonly versionsByDomain = signal<
    Partial<Record<PricingDomain, AdminDomainPricingVersionSummary[]>>
  >({});

  protected readonly displayCount = computed(() => {
    const map = this.versionsByDomain();
    return PRICING_DOMAINS.reduce(
      (sum, domain) => sum + (map[domain]?.length ?? 0),
      0,
    );
  });

  protected readonly timelineEntries = computed(() =>
    buildUnifiedPricingTimeline(this.versionsByDomain()),
  );

  protected readonly timelineSegments = computed(() => {
    const items = this.timelineEntries();
    return items.map((entry, index) => ({
      entry,
      isLast: index === items.length - 1,
    }));
  });

  constructor() {
    bindStaffPageTitleCount(
      this.embeddedInCabinetPage,
      computed(() => (this.loading() ? null : this.displayCount())),
    );
  }

  public ngOnInit(): void {
    this.loadAllDomains();
  }

  protected domainLink(domain: PricingDomain): string {
    return `${this.staffCabinetPath}/pricing/${domain}`;
  }

  protected versionLink(domain: PricingDomain, id: string): string {
    return `${this.staffCabinetPath}/pricing/${domain}/${id}`;
  }

  protected domainCount(domain: PricingDomain): number {
    return this.versionsByDomain()[domain]?.length ?? 0;
  }

  protected formatDate = formatPricingDate;
  protected formatRate = formatPricingRate;
  protected isCurrent = isPricingVersionCurrent;
  protected isFuture = isPricingVersionFuture;

  private loadAllDomains(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.partialLoadError.set(false);

    forkJoin(
      Object.fromEntries(
        PRICING_DOMAINS.map((domain) => [
          domain,
          this.#pricingApi.listVersions(domain).pipe(
            map((response) => ({ ok: true as const, response })),
            catchError(() => of({ ok: false as const, domain })),
          ),
        ]),
      ) as Record<PricingDomain, Observable<DomainListResult>>,
    ).subscribe({
      next: (results) => {
        const versionsByDomain: Partial<
          Record<PricingDomain, AdminDomainPricingVersionSummary[]>
        > = {};
        let failedCount = 0;

        for (const domain of PRICING_DOMAINS) {
          const result = results[domain];
          if (result.ok) {
            versionsByDomain[domain] = result.response.versions;
          } else {
            failedCount += 1;
            versionsByDomain[domain] = [];
          }
        }

        this.versionsByDomain.set(versionsByDomain);
        this.partialLoadError.set(failedCount > 0);
        this.loadError.set(failedCount === PRICING_DOMAINS.length);
        this.loading.set(false);
      },
      error: () => {
        this.loadError.set(true);
        this.partialLoadError.set(false);
        this.loading.set(false);
      },
    });
  }
}
